import { useCallback, useRef, useEffect } from "react";
import { AnyBlock, Point2D } from "../../types";
import { useBlocks } from "../../liveblocks/maps";
import {
  pixelToGridPosition,
  pixelToGridPositionSloppy,
} from "../../utils/gridPosition";
import cloneBlock from "../../blocks/cloneBlock";
import { useAtom } from "jotai";
import { blocksRefAtom, selectionAtom } from "../../jotai/atoms";
import { useSendAnalyticsEvent } from "../../analytics/events";
import { useUpdateMyPresence } from "@liveblocks/react";
import { findCursorType } from "../cursor/useCursorUpdater";

export function isValidMove(ev: MouseEvent | React.MouseEvent) {
  const { target } = ev;
  if (
    !(target instanceof Element && target.closest(".block")) ||
    (target instanceof Element && target.closest("button, input"))
  ) {
    return false;
  }
  // only on left click
  if (ev.button !== 0) return false;

  return true;
}

export default function useDragMove() {
  const [selection, setSelection] = useAtom(selectionAtom);
  const blockStartPosRef = useRef<Record<AnyBlock["id"], Point2D> | null>(null);
  const dragStartPosRef = useRef<Point2D | null>(null);
  /** Need to keep track of the pointer pos since scroll event doesn't report it. */
  const pointerPosInViewportRef = useRef<Point2D>({ x: 0, y: 0 });
  const { upsertBlock } = useBlocks();
  const [blocksRef] = useAtom(blocksRefAtom);
  const updateMyPresence = useUpdateMyPresence();
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const stateRef = useRef({ selection });
  stateRef.current = { selection };

  const updateBlock = useCallback(
    (...update: Partial<AnyBlock>[]) => {
      upsertBlock(update, true);
    },
    [upsertBlock]
  );

  const onMouseDown = useCallback(
    (ev: MouseEvent) => {
      if (!isValidMove(ev)) return;

      const { selection } = stateRef.current;

      // Duplicate blocks if alt/option is pressed when you click
      if (ev.altKey) {
        const selectedBlocks = Object.keys(selection).map(
          (id) => blocksRef.current[id]
        );
        selectedBlocks.sort((a, b) => a.updatedAt - b.updatedAt);
        const newBlocks = [];
        for (const selectedBlock of selectedBlocks) {
          const newBlock = cloneBlock(selectedBlock);
          newBlocks.push(newBlock);
          delete selection[selectedBlock.id];
          selection[newBlock.id] = Date.now();
          blocksRef.current[newBlock.id] = newBlock;
          sendAnalyticsEvent({
            name: "blockCreated",
            type: newBlock.type,
            pasted: true,
          });
        }

        upsertBlock(newBlocks, true);
        setSelection(selection);
      }

      const pointerPosInViewport = pointerPosInViewportRef.current;
      const pointerPosInSpace = {
        x: window.scrollX + pointerPosInViewport.x,
        y: window.scrollY + pointerPosInViewport.y,
      };
      dragStartPosRef.current = pointerPosInSpace;
      updateMyPresence({ cursor: "grabbing" });

      const blockPos: Record<AnyBlock["id"], Point2D> = Object.fromEntries(
        // @ts-ignore
        Object.keys(selection)
          .map((id) => {
            const b = blocksRef.current[id];
            if (!b) {
              return null;
            }
            // @ts-ignore
            return [id, b.position];
          })
          .filter((b) => b)
      );

      blockStartPosRef.current = blockPos;
    },
    [blocksRef, sendAnalyticsEvent, setSelection, updateMyPresence, upsertBlock]
  );

  const updatePos = useCallback(
    (event: Event) => {
      const blockStartPos = blockStartPosRef.current;
      const dragStartPos = dragStartPosRef.current;
      if (!dragStartPos || !blockStartPos) {
        return;
      }

      const pointerPosInViewport = pointerPosInViewportRef.current;
      const pointerPosInSpace = {
        x: window.scrollX + pointerPosInViewport.x,
        y: window.scrollY + pointerPosInViewport.y,
      };
      const draggedInPixel = {
        x: pointerPosInSpace.x - dragStartPos.x,
        y: pointerPosInSpace.y - dragStartPos.y,
      };

      // Donot update if it did not move
      // Check on pixel drag, which has no rounding effect
      if (draggedInPixel.x === 0 && draggedInPixel.y === 0) {
        return;
      }

      const draggedInGrid =
        event.type === "mousemove" || event.type === "scroll"
          ? pixelToGridPositionSloppy(draggedInPixel)
          : pixelToGridPosition(draggedInPixel, Math.round);

      const updates = Object.entries(blockStartPos)
        .sort(
          ([id1], [id2]) =>
            blocksRef.current[id1].updatedAt - blocksRef.current[id2].updatedAt
        )
        .map(([id, start]) => {
          const position = {
            x: start.x + draggedInGrid.x,
            y: start.y + draggedInGrid.y,
          };

          if (event.type === "mouseup") {
            // there were blocks with incorrect floating position
            // value from the start so here we make sure blocks always snapped
            position.x = Math.round(position.x);
            position.y = Math.round(position.y);
          }

          return { id, position };
        });

      if (updates.length > 0) {
        updateBlock(...updates);
      }
    },
    [blocksRef, updateBlock]
  );

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      pointerPosInViewportRef.current = { x: event.clientX, y: event.clientY };

      updatePos(event);
    },
    [updatePos]
  );

  const onMouseUp = useCallback(
    (event: MouseEvent) => {
      updatePos(event);

      if (event.target instanceof Element) {
        const cursor = findCursorType(event.target);
        updateMyPresence({ cursor });
      }

      blockStartPosRef.current = null;
      dragStartPosRef.current = null;
    },
    [updateMyPresence, updatePos]
  );

  const onScroll = useCallback(
    (event: Event) => {
      updatePos(event);
    },
    [updatePos]
  );

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("scroll", onScroll);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("scroll", onScroll);
    };
  }, [onMouseMove, onMouseUp, onScroll]);
  return onMouseDown;
}
